import { apiRequest } from "./authConfig";

// Strip trailing /api from the base URL since endpoints already include /api/
const rawBaseUrl =
  process.env.REACT_APP_API_URL ||
  "https://kosmoholm-api-h7h6h6e6fngce8fz.norwayeast-01.azurewebsites.net";
const API_BASE_URL = rawBaseUrl.replace(/\/api\/?$/, '');

/**
 * Decodes the payload of a JWT and returns true if the token has expired.
 * Falls back to true (expired) if the token cannot be decoded so that
 * the caller clears it and falls through to MSAL.
 */
function isTokenExpired(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return true;
    // exp is in seconds; add a 60-second buffer to avoid edge-case races
    return Date.now() >= (payload.exp - 60) * 1000;
  } catch {
    return true;
  }
}

/**
 * Makes an authenticated API call, attaching the appropriate Bearer token.
 * Supports both MSAL (Microsoft) and local JWT (Google/GitHub) tokens.
 * @param {object} msalInstance - The MSAL PublicClientApplication instance
 * @param {string} endpoint - API path (e.g. "/api/PropertiesDev")
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>}
 */
export async function apiFetch(msalInstance, endpoint, options = {}) {
  let token = null;

  // Check for local JWT first (Google/GitHub auth).
  // Decode the payload and verify the `exp` claim so we don't keep sending
  // an expired token (the backend would reject it with 401 anyway, but
  // clearing it here lets us fall through to MSAL or prompt re-login).
  const localToken = localStorage.getItem('kosmoholm-auth-token');
  if (localToken && !isTokenExpired(localToken)) {
    token = localToken;
  } else if (localToken) {
    // Token exists but is expired — clean up stale auth state
    localStorage.removeItem('kosmoholm-auth-token');
    localStorage.removeItem('kosmoholm-auth-user');
    localStorage.removeItem('kosmoholm-auth-provider');
  }

  if (!token) {
    // Fall back to MSAL token (Microsoft auth)
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error("No authenticated user. Please sign in.");
    }

    // The active account is the one the user actually signed in as. accounts[0] is
    // merely the oldest entry in the cache, so with two accounts signed in it would
    // silently call the API as the wrong person.
    const account = msalInstance.getActiveAccount() || accounts[0];

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...apiRequest,
        account,
      });
      token = tokenResponse.accessToken;
    } catch (error) {
      console.warn("Silent token acquisition failed, redirecting to login:", error.message);
      await msalInstance.acquireTokenRedirect({ ...apiRequest, account });
      return; // Page will redirect
    }
  }

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  // Only set Content-Type for non-FormData bodies
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  const url = endpoint.startsWith("http")
    ? endpoint
    : `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers,
  });

  return response;
}

/**
 * Convenience wrapper around apiFetch that checks res.ok and parses JSON.
 * Returns the parsed JSON body, or null for empty responses (e.g. 204).
 * Throws on non-ok responses with the server error message.
 *
 * USE THIS for all standard API calls. Only use raw apiFetch() when you
 * need streaming, blob responses, or custom response handling.
 */
export async function apiFetchJson(msalInstance, endpoint, options = {}) {
  const res = await apiFetch(msalInstance, endpoint, options);
  if (!res.ok) {
    const errorText = await res.text().catch(() => '');
    throw new Error(errorText || `API error: ${res.status} ${res.statusText}`);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}
